/**
 * A conversation: header, call bar, transcript, composer — the desktop app's
 * parts in the same order, with the bar between the header and the transcript
 * because that is where a running call belongs: under what it is a call in,
 * over what was said before it started.
 *
 * Beside them, the thread named in the address (`?thread=<root>`), docked to
 * the right on a wide window and in place of the conversation on a narrow one.
 * Selecting another conversation drops the parameter, so a thread never sits
 * beside a conversation it is not in.
 *
 * This page holds what is set from a transcript row and acted on elsewhere:
 * the quote the composer carries.
 *
 * **Opening a conversation reads it.** `markRead` runs on the store's newest
 * seq — thread replies, edits and reactions included, which is the cursor the
 * desktop's `markRead` sends — so an open conversation never carries an unread
 * badge. The desktop app's one deliberate deviation from its design bundle,
 * for the same reason.
 */

import { useEffect, useState } from 'react';
import { useT } from '../i18n';
import { useRouter } from '../router';
import { store, useStore } from '../lib/relay/store';
import { CallBar } from '../components/CallBar';
import { Composer, type Reference } from '../components/chat/Composer';
import { mentionCandidates } from '../components/chat/mention';
import { ConversationHeader } from '../components/chat/ConversationHeader';
import { ReadOnlyNotice } from '../components/chat/Official';
import { ThreadPane } from '../components/chat/ThreadPane';
import { Transcript } from '../components/chat/Transcript';
import { Empty, useNarrow } from '../components/Shell';
import type { Message } from '../lib/relay/types';
import '../components/chat/chat.css';

/** The width under which the thread pane takes the conversation's place:
 *  docked any narrower, it leaves the transcript too little to read. */
const THREAD_BESIDE = 1080;

export function ConversationPage({ id }: { id: string }) {
  const t = useT();
  const narrow = useNarrow(THREAD_BESIDE);
  const { query, navigate } = useRouter();
  const threadId = query.get('thread') ?? undefined;

  const conversation = useStore((s) => s.conversations().find((c) => c.id === id));
  const messages = useStore((s) => s.messages(id));
  const people = useStore((s) => s.people(conversation?.members ?? []));
  const me = useStore((s) => s.me());
  const newest = useStore((s) => s.newestSeq(id));
  const [reference, setReference] = useState<Reference | undefined>(undefined);

  useEffect(() => {
    if (newest > 0) store.markRead(id, newest);
  }, [id, newest]);

  // The quote belongs to the conversation it was made in.
  useEffect(() => {
    setReference(undefined);
  }, [id]);

  if (!conversation) return <Empty title={t('app.notfound.title')} />;

  const nameOf = (message: Message) =>
    people.find((p) => p.id === message.senderId)?.name ?? '';

  const openThread = (message: Message) =>
    navigate(`/c/${id}?thread=${encodeURIComponent(message.id)}`, { keepScroll: true });

  const thread = threadId ? (
    <ThreadPane
      conversation={conversation}
      rootId={threadId}
      people={people}
      docked={!narrow}
    />
  ) : null;

  if (narrow && thread) return thread;

  return (
    <div style={{ display: 'flex', height: '100%', minHeight: 0 }}>
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0, minHeight: 0 }}>
        <ConversationHeader conversation={conversation} />
        <CallBar conversationId={id} />
        <Transcript
          conversationId={id}
          messages={messages}
          people={people}
          onReply={(message) => setReference({ message, name: nameOf(message) })}
          onThread={openThread}
        />
        {conversation.official ? (
          <ReadOnlyNotice />
        ) : (
          <Composer
            // A new conversation gets a new composer: picked files and an
            // open picker must not carry over into it.
            key={id}
            conversationId={id}
            {...(reference ? { reference } : {})}
            onClearReference={() => setReference(undefined)}
            candidates={mentionCandidates(people, me?.id)}
          />
        )}
      </div>
      {thread}
    </div>
  );
}
